import React, { useEffect, useRef, useState } from 'react'

const Header: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [activeSection, setActiveSection] = useState('home')
  const [scrolled, setScrolled] = useState(false)
  const observerRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const navLinks = [
    { label: 'Diensten', href: '#diensten', id: 'diensten' },
    { label: 'Werkwijze', href: '#werkwijze', id: 'werkwijze' },
  ]

  useEffect(() => {
    const sectionIds = ['home', 'diensten', 'werkwijze', 'contact']

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)
        if (visible.length > 0) {
          setActiveSection(visible[0].target.id)
        }
      },
      { threshold: 0.3 },
    )

    sectionIds.forEach((id) => {
      const el = document.getElementById(id)
      if (el) observerRef.current?.observe(el)
    })

    return () => observerRef.current?.disconnect()
  }, [])

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] bg-brand-accent text-white text-sm font-semibold px-4 py-2 rounded-full"
      >
        Ga naar inhoud
      </a>
      <header
        className={`sticky top-0 z-50 bg-brand-primary/95 backdrop-blur-sm transition-all duration-300 ${
          scrolled ? 'border-b border-brand-ink/10' : ''
        }`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            <a
              href="#home"
              className="flex items-center gap-2 font-bold text-base text-brand-ink"
            >
              <img src="/logo.svg" alt="LeanC logo" className="w-25 h-10" />
            </a>

            {/* Desktop Navigation */}
            <nav
              className="hidden md:flex items-center gap-5"
              aria-label="Hoofdmenu"
            >
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className={`text-sm transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 focus-visible:ring-offset-brand-primary rounded-sm ${
                    activeSection === link.id
                      ? 'text-brand-accent font-semibold'
                      : 'text-brand-muted hover:text-brand-ink'
                  }`}
                  aria-current={activeSection === link.id ? 'true' : undefined}
                >
                  {link.label}
                </a>
              ))}
              <a
                href="#contact"
                className={`text-sm font-semibold px-4 py-2 rounded-full transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 focus-visible:ring-offset-brand-primary ${
                  activeSection === 'contact'
                    ? 'bg-brand-accent/90 text-white'
                    : 'bg-brand-accent text-white hover:bg-brand-accent/85'
                }`}
                aria-current={activeSection === 'contact' ? 'true' : undefined}
              >
                Contact
              </a>
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden px-3 py-3 text-brand-muted hover:text-brand-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 focus-visible:ring-offset-brand-primary rounded-sm"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Menu sluiten' : 'Menu openen'}
              aria-expanded={mobileMenuOpen}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {mobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <nav
              className="md:hidden py-3 space-y-2 anim-slide-down"
              aria-label="Mobiel menu"
            >
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className={`block text-sm transition-colors duration-200 py-1 ${
                    activeSection === link.id
                      ? 'text-brand-accent font-semibold'
                      : 'text-brand-muted hover:text-brand-ink'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                  aria-current={activeSection === link.id ? 'true' : undefined}
                >
                  {link.label}
                </a>
              ))}
              <a
                href="#contact"
                className="block bg-brand-accent text-white text-sm font-semibold px-4 py-2 rounded-full hover:bg-brand-accent/85 transition-all duration-200 text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                Contact
              </a>
            </nav>
          )}
        </div>
      </header>
    </>
  )
}

export default Header
